import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Copy, Lock, Users, Wallet, Crown, Gem, ExternalLink, KeyRound, Trophy, ImagePlus, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import InvitePoster from "@/components/InvitePoster";
import UserAvatar from "@/components/UserAvatar";
import WinMarquee from "@/components/WinMarquee";

function MiniAvatar({ name, svip, king }) {
  return (
    <span className="relative inline-grid place-items-center w-9 h-8 shrink-0" data-testid={king ? "mini-avatar-king" : undefined}>
      <UserAvatar
        name={name}
        size={26}
        className={svip ? "ring-1 ring-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.45)]" : "opacity-90"}
      />
      {king && (
        <Crown
          className="absolute -top-2 -right-1 w-3.5 h-3.5 text-[#D4AF37] drop-shadow-[0_0_4px_rgba(212,175,55,0.9)]"
          fill="#D4AF37"
        />
      )}
    </span>
  );
}

function copyText(text, label) {
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(`${label}已复制`))
    .catch(() => toast.error("复制失败，请手动复制"));
}

function ProgressBar({ value, max, testid }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div>
      <div className="flex justify-between text-xs text-gray-500 mb-2 font-mono-num">
        <span>{value} / {max} 人</span>
        <span>{pct.toFixed(0)}%</span>
      </div>
      <div className="h-1.5 bg-[#1A1A24] overflow-hidden" data-testid={testid}>
        <motion.div
          className="h-full bg-gradient-to-r from-[#AA7C11] via-[#D4AF37] to-[#F3E5AB]"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </div>
  );
}

function GroupCard({ level, title, threshold, count, group, qr, testid }) {
  const unlocked = !!group;
  return (
    <div data-testid={testid} className="panel relative p-6 lg:p-8 overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif-sc text-xl font-bold flex items-center gap-2">
          {level === "vip" ? <Crown className="w-5 h-5 text-[#D4AF37]" /> : level === "svip" ? <Gem className="w-5 h-5 text-[#F3E5AB]" /> : <Users className="w-5 h-5 text-[#D4AF37]" />}
          {title}
        </h3>
        <span
          className={`text-[10px] tracking-[0.2em] px-2.5 py-1 border ${
            unlocked ? "text-[#D4AF37] border-[#D4AF37]/50" : "text-gray-500 border-gray-700"
          }`}
        >
          {unlocked ? "已解锁" : `邀请满${threshold}人解锁`}
        </span>
      </div>

      {unlocked ? (
        <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
          {qr && (
            <img
              src={qr}
              alt={`${title}二维码`}
              data-testid="group-qr-code"
              className="w-32 h-32 border border-[#D4AF37]/40 p-1.5 bg-white"
            />
          )}
          <div className="flex-1">
            <p className="text-xs text-gray-500 mb-1 tracking-widest">群ID</p>
            <div className="flex items-center gap-2 mb-4">
              <span className="font-mono-num text-lg text-[#D4AF37]">{group.group_id}</span>
              <button
                onClick={() => copyText(group.group_id, "群ID")}
                className="text-gray-500 hover:text-[#D4AF37] transition-colors"
                aria-label="复制群ID"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
            <a
              href={group.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="group-join-btn"
              className="btn-gold inline-flex items-center gap-2 px-6 py-2.5 text-sm"
            >
              直达群链接 <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      ) : (
        <div className="relative">
          <div className="flex gap-6 items-center blur-md select-none opacity-40" aria-hidden>
            <div className="w-32 h-32 border border-[#D4AF37]/30 bg-[repeating-conic-gradient(#D4AF37_0%_25%,transparent_0%_50%)] bg-[length:16px_16px]" />
            <div>
              <p className="font-mono-num text-lg text-gray-400 mb-4">HY-****-***</p>
              <span className="btn-ghost-gold px-6 py-2.5 text-sm inline-block">直达群链接</span>
            </div>
          </div>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center bg-[#070709]/80 backdrop-blur-sm px-6 py-4 border border-[#D4AF37]/20">
              <Lock className="w-5 h-5 text-[#D4AF37] mx-auto mb-2" />
              <p className="text-sm text-gray-300">
                还差 <span className="text-[#D4AF37] font-mono-num">{Math.max(threshold - count, 0)}</span> 人解锁
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Invite() {
  const { user, ready, openAuth, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState([]);
  const [qrs, setQrs] = useState({});
  const [board, setBoard] = useState(null);
  const [showcase, setShowcase] = useState([]);
  const [subBoard, setSubBoard] = useState(null);
  const [lbPeriod, setLbPeriod] = useState("all");
  const [posterOpen, setPosterOpen] = useState(false);
  const [cardKey, setCardKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/invite/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/invite/records").then((r) => setRecords(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user?.is_activated) return;
    api.get(`/invite/leaderboard?period=${lbPeriod}`).then((r) => setBoard(r.data)).catch(() => {});
    api.get("/submissions/showcase").then((r) => setShowcase(r.data)).catch(() => {});
    api.get("/submissions/leaderboard").then((r) => setSubBoard(r.data)).catch(() => {});
  }, [user, lbPeriod]);

  useEffect(() => {
    if (!stats) return;
    Object.keys(stats.groups).forEach((lvl) => {
      api.get(`/invite/qr/${lvl}`, { responseType: "blob" })
        .then((r) => setQrs((q) => ({ ...q, [lvl]: URL.createObjectURL(r.data) })))
        .catch(() => {});
    });
  }, [stats]);

  if (!ready) return <div className="min-h-screen" />;

  if (!user) {
    return (
      <main className="min-h-screen grid place-items-center px-5 pt-16">
        <div className="text-center">
          <KeyRound className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif-sc text-3xl font-bold mb-4">邀请大厅仅限会员</h1>
          <p className="text-gray-400 text-sm mb-8">请先注册或登录会员账号</p>
          <button data-testid="invite-login-btn" onClick={() => openAuth()} className="btn-gold px-8 py-3 text-sm">
            注册 / 登录
          </button>
        </div>
      </main>
    );
  }

  const redeem = async (e) => {
    e.preventDefault();
    if (!cardKey.trim()) return toast.error("请输入卡密");
    setBusy(true);
    try {
      await api.post("/keys/redeem", { card_key: cardKey.trim() });
      await refresh();
      toast.success("卡密激活成功，邀请奖励已开启");
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (!user.is_activated) {
    return (
      <main className="min-h-screen grid place-items-center px-5 pt-16" data-testid="invite-activation-gate">
        <div className="panel p-8 lg:p-10 max-w-xl w-full text-center gold-glow">
          <KeyRound className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif-sc text-2xl lg:text-3xl font-bold mb-3">激活卡密，开启邀请奖励</h1>
          <p className="text-gray-400 text-sm mb-8 leading-relaxed">
            激活会员进入项目交流群 · 每日分享日赚500+稳定项目<br />
            每邀请1位成员立得 ¥128 · 满10人进VIP群 · 满30人进超级VIP群
          </p>
          <form onSubmit={redeem} className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              data-testid="invite-card-key-input"
              value={cardKey}
              onChange={(e) => setCardKey(e.target.value)}
              placeholder="HY-XXXX-XXXX-XXXX"
              className="input-gold flex-1 px-4 py-3 text-sm font-mono-num tracking-widest uppercase text-center"
            />
            <button data-testid="invite-redeem-btn" type="submit" disabled={busy} className="btn-gold px-6 py-3 text-sm whitespace-nowrap">
              立即激活
            </button>
          </form>
          <p className="text-xs text-gray-500">
            没有卡密？
            <Link to="/card-guide" data-testid="invite-card-guide-link" className="text-[#D4AF37] underline underline-offset-4 decoration-[#D4AF37]/40 hover:decoration-[#D4AF37] transition-colors">
              查看图文获取教程
            </Link>
            ，淘宝搜索「合赢项目交流社区」卡密购买后激活
          </p>
        </div>
      </main>
    );
  }

  const inviteLink = stats ? `${window.location.origin}/?ref=${stats.invite_code}` : "";

  return (
    <main className="min-h-screen pt-28 pb-24 px-5 lg:px-8" data-testid="invite-dashboard">
      <div className="mx-auto max-w-6xl">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#D4AF37] mb-4">INVITE HALL · 邀请大厅</p>
          <h1 className="font-serif-sc text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-2">
            邀请好友，<span className="text-gold-grad">共赢奖励</span>
          </h1>
          <p className="text-gray-400 text-sm mb-6">每邀请1位成员立得 ¥128 · 满10人进VIP项目交流群 · 满30人进超级VIP项目交流群</p>
        </motion.div>

        <div className="mb-10">
          <WinMarquee testid="invite-win-marquee" />
        </div>

        {stats && (
          <>
            <div className="grid grid-cols-3 gap-px bg-[#D4AF37]/10 border border-[#D4AF37]/10 mb-10">
              {[
                { icon: Users, label: "已邀请成员", value: `${stats.invite_count} 人`, testid: "invite-count-display" },
                { icon: Wallet, label: "累计邀请奖励", value: `¥ ${stats.reward_balance.toLocaleString()}`, testid: "invite-reward-display" },
                { icon: Crown, label: "当前等级", value: stats.level_name, testid: "invite-level-display" },
              ].map((s) => (
                <div key={s.label} className="bg-[#0A0A0E] p-3 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                  <s.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-gray-500 tracking-widest mb-1">{s.label}</p>
                    <p className="font-mono-num text-sm sm:text-xl text-[#F3E5AB] break-all" data-testid={s.testid}>{s.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="panel p-6 lg:p-8 mb-10">
              <p className="text-xs text-gray-500 tracking-widest mb-3">我的专属邀请链接</p>
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <code className="input-gold flex-1 px-4 py-3 text-sm font-mono-num text-[#F3E5AB] truncate" data-testid="invite-link-display">
                  {inviteLink}
                </code>
                <button
                  data-testid="invite-link-copy-btn"
                  onClick={() => copyText(inviteLink, "邀请链接")}
                  className="btn-gold px-6 py-3 text-sm inline-flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" /> 复制邀请链接
                </button>
                <button
                  data-testid="poster-generate-btn"
                  onClick={() => setPosterOpen(true)}
                  className="btn-ghost-gold px-6 py-3 text-sm inline-flex items-center justify-center gap-2"
                >
                  <ImagePlus className="w-4 h-4" /> 生成邀请海报
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-sm mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-[#D4AF37]" /> VIP项目交流群进度</p>
                  <ProgressBar value={stats.invite_count} max={stats.vip_threshold} testid="vip-progress-bar" />
                </div>
                <div>
                  <p className="text-sm mb-3 flex items-center gap-2"><Gem className="w-4 h-4 text-[#F3E5AB]" /> 超级VIP项目交流群进度</p>
                  <ProgressBar value={stats.invite_count} max={stats.svip_threshold} testid="super-vip-progress-bar" />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <GroupCard level="normal" title="普通会员 · 海鸥项目交流群" threshold={0} count={stats.invite_count} group={stats.groups.normal} qr={qrs.normal} testid="normal-group-card" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-10">
              <GroupCard level="vip" title="VIP项目交流群" threshold={stats.vip_threshold} count={stats.invite_count} group={stats.groups.vip} qr={qrs.vip} testid="vip-group-card" />
              <GroupCard level="svip" title="超级VIP项目交流群" threshold={stats.svip_threshold} count={stats.invite_count} group={stats.groups.svip} qr={qrs.svip} testid="super-vip-group-card" />
            </div>

            <div className="panel p-6 lg:p-8 mb-10" data-testid="join-group-guide">
              <h3 className="font-serif-sc text-lg font-bold mb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#D4AF37]" /> 海鸥群 · 入群指引
              </h3>
              <p className="text-xs text-gray-500 mb-6">「海鸥项目交流群」已随卡密激活自动解锁，按以下三步加入（海鸥加密群，信息更安全）</p>
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="border border-[#D4AF37]/15 bg-[#0F0F14] p-5" data-testid="guide-step-download">
                  <span className="font-mono-num text-2xl font-black text-gold-grad">01</span>
                  <p className="text-sm font-bold mt-2 mb-2">下载海鸥 APP</p>
                  <p className="text-xs text-gray-500 leading-relaxed mb-4">海鸥加密聊天软件，支持 iOS / 安卓 / 电脑多端，点击下方按钮前往官网下载安装</p>
                  <a
                    href={stats.chat_app_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="seagull-download-btn"
                    className="btn-gold w-full py-2.5 text-xs inline-flex items-center justify-center gap-2 mb-4"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> 下载海鸥 APP（官网）
                  </a>
                  <div className="border border-[#D4AF37]/20 bg-[#070709] p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F3E5AB] to-[#AA7C11] grid place-items-center text-[#070709] font-serif-sc font-black">鸥</div>
                    <div className="flex-1">
                      <p className="text-[11px] text-gray-200">海鸥 · Haiou</p>
                      <p className="text-[9px] text-gray-600">端到端加密 · 群聊 · 多端同步</p>
                    </div>
                  </div>
                </div>
                <div className="border border-[#D4AF37]/15 bg-[#0F0F14] p-5" data-testid="guide-step-join">
                  <span className="font-mono-num text-2xl font-black text-gold-grad">02</span>
                  <p className="text-sm font-bold mt-2 mb-2">扫码 / 搜群ID / 点链接</p>
                  <p className="text-xs text-gray-500 leading-relaxed mb-4">
                    打开海鸥「扫一扫」扫上方群二维码，或在搜索框输入群ID
                    <span className="font-mono-num text-[#D4AF37]"> {stats.groups.normal?.group_id} </span>
                    ，也可直接点「直达群链接」
                  </p>
                  <div className="border border-[#D4AF37]/20 bg-[#070709] p-3">
                    <div className="relative h-16 grid place-items-center">
                      <span className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#D4AF37]" />
                      <span className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#D4AF37]" />
                      <span className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#D4AF37]" />
                      <span className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#D4AF37]" />
                      <span className="text-[10px] text-gray-500 font-mono-num tracking-widest">对准群二维码扫描</span>
                    </div>
                  </div>
                </div>
                <div className="border border-[#D4AF37]/15 bg-[#0F0F14] p-5" data-testid="guide-step-verify">
                  <span className="font-mono-num text-2xl font-black text-gold-grad">03</span>
                  <p className="text-sm font-bold mt-2 mb-2">填写合赢账户申请</p>
                  <p className="text-xs text-gray-500 leading-relaxed mb-4">
                    申请入群时填写您的合赢账户 <span className="font-mono-num text-[#D4AF37]">{user.username}</span>，管理员核验会员身份后放行
                  </p>
                  <div className="border border-[#D4AF37]/20 bg-[#070709] p-3 flex flex-col gap-2">
                    <div className="border border-[#D4AF37]/30 px-2.5 py-1.5">
                      <span className="text-[10px] font-mono-num text-[#F3E5AB]">{user.username}</span>
                    </div>
                    <div className="bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] text-center py-1.5">
                      <span className="text-[10px] text-[#070709] font-bold">申请加入</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-gray-600 leading-relaxed border-t border-[#D4AF37]/10 pt-4">
                注意：海鸥群申请必须填写本人的合赢账户（注册手机号），账户不符将无法通过审核；群二维码与链接仅限本人使用，请勿外传
              </p>
            </div>

            {board && (
              <div className="panel p-6 lg:p-8 mb-10" data-testid="invite-leaderboard">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <h3 className="font-serif-sc text-lg font-bold flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#D4AF37]" /> 邀请排行榜
                  </h3>
                  <div className="flex gap-2">
                    {[["all", "总榜"], ["week", "本周榜"], ["month", "本月榜"]].map(([p, label]) => (
                      <button
                        key={p}
                        data-testid={`lb-tab-${p}`}
                        onClick={() => setLbPeriod(p)}
                        className={`text-xs tracking-widest px-4 py-1.5 border transition-colors ${
                          lbPeriod === p
                            ? "border-[#D4AF37]/60 text-[#D4AF37]"
                            : "border-[#D4AF37]/15 text-gray-500 hover:text-gray-300"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-5 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs border border-[#D4AF37]/15 bg-[#D4AF37]/5 px-4 py-3">
                  <span className="text-[#D4AF37]">周榜榜首额外奖励 ¥{board.weekly_top_reward ?? 388}</span>
                  <span className="text-[#D4AF37]">月榜榜首额外奖励 ¥{board.monthly_top_reward ?? 1288}</span>
                  <span className="text-gray-500">周期结束后自动结算发放，奖励计入可提现余额</span>
                </div>
                {board.my_rank && (
                  <div className="mb-4 text-xs text-gray-500 font-mono-num text-right" data-testid="my-rank-display">
                    我的排名 NO.{board.my_rank}
                  </div>
                )}
                {board.top.length === 0 ? (
                  <p className="text-sm text-gray-500" data-testid="leaderboard-empty">虚位以待，第一名就是你</p>
                ) : (
                  <div className="divide-y divide-[#D4AF37]/10" data-testid="leaderboard-list">
                    {board.top.map((r) => (
                      <div
                        key={r.rank}
                        className={`py-3 flex items-center gap-3 sm:gap-4 ${
                          r.is_me ? "bg-[#D4AF37]/5 -mx-3 px-3 border border-[#D4AF37]/25" : ""
                        }`}
                      >
                        <span
                          className={`font-mono-num w-8 text-center text-lg ${
                            r.rank <= 3 ? "text-gold-grad font-bold" : "text-gray-500"
                          }`}
                        >
                          {String(r.rank).padStart(2, "0")}
                        </span>
                        <MiniAvatar name={r.username} svip={r.invite_count >= (stats?.svip_threshold ?? 30)} king={r.rank === 1} />
                        <span className="flex-1 text-sm text-gray-200 font-mono-num truncate">
                          {r.username}
                          {r.is_me && (
                            <span className="ml-2 text-[10px] text-[#D4AF37] border border-[#D4AF37]/40 px-1.5 py-0.5">我</span>
                          )}
                        </span>
                        <span className="hidden sm:inline text-xs text-gray-500">{r.level_name}</span>
                        <span className="font-mono-num text-sm text-[#F3E5AB] w-14 text-right">{r.invite_count} 人</span>
                        <span className="font-mono-num text-sm text-[#D4AF37] w-20 text-right">
                          ¥{(r.invite_count * 128).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="panel p-6 lg:p-8 mb-10" data-testid="submission-showcase">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <h3 className="font-serif-sc text-lg font-bold flex items-center gap-2">
                  <BadgeCheck className="w-5 h-5 text-[#D4AF37]" /> 会员投稿 · 录用光荣榜
                </h3>
                <a
                  href="/#projects"
                  data-testid="showcase-submit-cta"
                  className="text-xs text-[#D4AF37] border border-[#D4AF37]/40 px-4 py-1.5 hover:bg-[#D4AF37]/10 transition-colors"
                >
                  我也要投稿 · 奖励最高 ¥188,888
                </a>
              </div>
              <p className="text-xs text-gray-500 mb-6">会员投稿被平台录用后，奖励最高 ¥188,888 实时到账可提现 USDT，项目同步上架首页「今日项目」</p>
              {showcase.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="showcase-empty">虚位以待，第一个被录用的会员就是你</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="showcase-list">
                  {showcase.map((s, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.08 }}
                      className="border border-[#D4AF37]/20 bg-[#0F0F14] p-5"
                      data-testid={`showcase-card-${i}`}
                    >
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <span className="font-mono-num text-xs text-gray-500">{s.username} · {s.date}</span>
                        <span className={`text-[10px] tracking-widest px-2 py-0.5 border shrink-0 ${
                          s.on_shelf ? "text-emerald-400 border-emerald-500/40" : "text-gray-500 border-gray-700"
                        }`}>
                          {s.on_shelf ? "已上架今日项目" : "已录用"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-100 font-bold mb-2">{s.title}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="text-[10px] font-mono-num text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-0.5">日赚{s.daily_income}</span>
                        {s.tags.map((t) => (
                          <span key={t} className="text-[10px] text-gray-500 border border-[#D4AF37]/10 px-2 py-0.5">{t}</span>
                        ))}
                      </div>
                      <div className="border-t border-dashed border-[#D4AF37]/20 pt-3 flex items-center justify-between">
                        <span className="text-[10px] tracking-widest text-gray-500">录用奖励 · 已到账可提现</span>
                        <span className="font-mono-num text-xl text-gold-grad font-bold">+¥{s.reward.toLocaleString()}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {subBoard && (
              <div className="panel p-6 lg:p-8 mb-10" data-testid="submit-leaderboard">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <h3 className="font-serif-sc text-lg font-bold flex items-center gap-2">
                    <BadgeCheck className="w-5 h-5 text-[#D4AF37]" /> 投稿龙虎榜
                  </h3>
                  <span className="text-xs text-[#D4AF37] border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-4 py-2">
                    周榜投稿王 ¥{subBoard.weekly_king_reward.toLocaleString()} · 月榜投稿王 ¥{subBoard.monthly_king_reward.toLocaleString()} · 周期结束自动结算
                  </span>
                </div>
                {subBoard.top.length === 0 ? (
                  <p className="text-sm text-gray-500" data-testid="submit-board-empty">暂无录用记录，去首页投稿抢占榜首</p>
                ) : (
                  <div className="divide-y divide-[#D4AF37]/10" data-testid="submit-board-list">
                    {subBoard.top.map((r) => (
                      <div key={r.rank} className="py-3 flex items-center gap-3 sm:gap-4" data-testid={`submit-board-row-${r.rank}`}>
                        <span className={`font-mono-num w-8 text-center text-lg ${r.rank <= 3 ? "text-gold-grad font-bold" : "text-gray-500"}`}>
                          {String(r.rank).padStart(2, "0")}
                        </span>
                        <MiniAvatar name={r.username} svip={r.is_svip} king={r.rank === 1} />
                        <span className="flex-1 text-sm text-gray-200 font-mono-num truncate">
                          {r.username}
                          {r.rank === 1 && (
                            <span className="ml-2 text-[10px] text-[#070709] bg-[#D4AF37] px-1.5 py-0.5">投稿王</span>
                          )}
                        </span>
                        <span className="hidden sm:inline text-xs text-gray-500">本月录用 {r.month_count} 篇</span>
                        <span className="font-mono-num text-sm text-[#F3E5AB] w-16 text-right">{r.approved_count} 篇</span>
                        <span className="font-mono-num text-sm text-[#D4AF37] w-24 text-right">¥{r.reward_total.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="panel p-6 lg:p-8">
              <h3 className="font-serif-sc text-lg font-bold mb-6">邀请记录</h3>
              {records.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="invite-records-empty">暂无邀请记录，分享上方链接开始赚取奖励</p>
              ) : (
                <div className="divide-y divide-[#D4AF37]/10" data-testid="invite-records-list">
                  {records.map((r, i) => (
                    <div key={i} className="py-3 flex items-center justify-between text-sm">
                      <span className="text-gray-300">{r.invitee_username}</span>
                      <span className="font-mono-num text-[#D4AF37]">+¥{r.reward}</span>
                      <span className="text-xs text-gray-600 font-mono-num">{(r.created_at || "").slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        {stats && (
          <InvitePoster
            open={posterOpen}
            onClose={() => setPosterOpen(false)}
            inviteLink={inviteLink}
            inviteCode={stats.invite_code}
          />
        )}
      </div>
    </main>
  );
}
